import { FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EditorialPage, handwritten } from "../components/editorial";
import { captureConversion } from "../lib/analytics";
import {
  ChoiceInput,
  FEATURE_OPTIONS,
  Honeypot,
  MultiChoiceInput,
  ScaleInput,
  TextInput,
  segmentToEnum,
  submitSurvey,
  usageAmountToEnum,
} from "../components/survey/SurveyInputs";

// /beta-review/short — LL-0184, the 10-question "highest signal per question"
// variant. Not per-group like /beta-review (A/C/D) — universal, same as
// /beta-review/full, submits group="FULL" with form_variant="short10" so it's
// distinguishable in the data without a DB migration (study_group already
// allows FULL). Unlisted, same as the other two.
//
// Per Adrien's ask (2026-07-06): if capped at 10 questions, which give the
// most learning per question asked? Selected for maximum signal density,
// each one earning its slot rather than being "nice to have":
// 1. USE1  — usage-amount: without this, every answer below is unreadable
//    (a "not disappointed" from someone who opened it once means something
//    totally different from a daily user's). Asked first as context.
// 2. SEG1  — segmentation: same reasoning, who's answering matters as much
//    as what they answer.
// 3. What did you use it for — real behavior, not stated intent.
// 4. Ease of the main task — the single most actionable UX-friction number.
// 5. What confused you / what's missing — the highest-value open-text
//    question for "what to fix," by a wide margin over any other open Q.
// 6. Bug/crash report — cheap to ask, directly actionable for engineering,
//    and distinguishes "broken" from "just didn't like it."
// 7. Most valuable feature — the "aha" question; cross-tabbed against PMF
//    (Q8) this tells you what to double down on, Superhuman's own method.
// 8. PMF (Sean Ellis) — the one externally-benchmarkable product-market-fit
//    number that exists across the whole survey design.
// 9. Referral likelihood — the standard growth/NPS-style signal.
// 10. Anything else — cheap insurance against an unknown unknown; people
//    volunteer things here (trust concerns, pricing reactions, etc) that a
//    fixed question list at only 10 slots can't otherwise ask about
//    directly. Deliberately last so it closes on whatever's most top of mind.
//
// Cut to make room (all present on /beta-review/full for anyone who wants
// the deeper version): willingness-to-pay, trust/safety scale, return_score,
// before/after positioning check, source attribution. None dropped because
// they're unimportant — cut because 10 questions means only the questions
// that change what you'd DO next make the list.

type ScoreField = "ease_score" | "referral_score";

type Question =
  | { id: string; kind: "text"; prompt: string }
  | {
      id: string;
      kind: "scale";
      prompt: string;
      lowLabel: string;
      highLabel: string;
      score: ScoreField;
    }
  | { id: string; kind: "choice"; prompt: string; options: string[] }
  | { id: string; kind: "multichoice"; prompt: string; options: string[] };

const QUESTIONS: Question[] = [
  {
    id: "USE1",
    kind: "choice",
    prompt:
      "Since you got the app, how many times would you say you've opened it?",
    options: ["Just the once", "A handful of times", "Regularly"],
  },
  {
    id: "SEG1",
    kind: "choice",
    prompt: "Which of these is closest to you right now?",
    options: [
      "New to London",
      "Have a friend group but want more to do",
      "Struggle to make plans happen",
      "Just curious",
    ],
  },
  {
    id: "F10",
    kind: "multichoice",
    prompt: "What did you actually end up using the app for?",
    options: FEATURE_OPTIONS,
  },
  {
    id: "S4",
    kind: "scale",
    prompt: "How easy was it to do the main thing you wanted to do?",
    lowLabel: "Really hard",
    highLabel: "Effortless",
    score: "ease_score",
  },
  {
    id: "S5",
    kind: "text",
    prompt: "What confused you, or what's missing that you expected to find?",
  },
  {
    id: "BUG1",
    kind: "choice",
    prompt:
      "Did you run into any bugs, crashes, freezes, or things that just didn't work?",
    options: [
      "No, nothing like that",
      "Yes, something didn't work right",
      "Not sure / can't remember",
    ],
  },
  {
    id: "FEAT1",
    kind: "multichoice",
    prompt: "Which one thing felt most worth having?",
    options: FEATURE_OPTIONS,
  },
  {
    id: "PMF1",
    kind: "choice",
    prompt: "How would you feel if you could no longer use LetsLoop?",
    options: [
      "Very disappointed",
      "Somewhat disappointed",
      "Not disappointed",
      "N/A, I don't really use it",
    ],
  },
  {
    id: "S9",
    kind: "scale",
    prompt: "How likely are you to tell a friend about it?",
    lowLabel: "No chance",
    highLabel: "Definitely",
    score: "referral_score",
  },
  {
    id: "S10",
    kind: "text",
    prompt: "Anything else you want to tell us, good or bad?",
  },
];

const SurveyShortPage = () => {
  const [searchParams] = useSearchParams();
  const name = (searchParams.get("n") || "").trim();
  // Stable tester identifier, independent of the name above — see LL-0184
  // NORTH-STAR.md. This form has no editable name field, so it matters less
  // here than on the full form, but still threaded through for consistency.
  const slug = (searchParams.get("s") || "").trim();

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [multiAnswers, setMultiAnswers] = useState<Record<string, string[]>>(
    {},
  );
  const [scores, setScores] = useState<Record<ScoreField, number | null>>({
    ease_score: null,
    referral_score: null,
  });
  const [honeypot, setHoneypot] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">(
    "idle",
  );

  useEffect(() => {
    document.title = "LetsLoop feedback";
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    try {
      const combinedAnswers: Record<string, string> = { ...answers };
      for (const [id, vals] of Object.entries(multiAnswers) as [
        string,
        string[],
      ][]) {
        if (vals.length > 0) combinedAnswers[id] = vals.join(", ");
      }
      const ok = await submitSurvey({
        group: "FULL",
        formVariant: "short10",
        testerName: name,
        testerSlug: slug,
        easeScore: scores.ease_score,
        referralScore: scores.referral_score,
        usageAmount: usageAmountToEnum(answers.USE1),
        segment: segmentToEnum(answers.SEG1),
        answers: combinedAnswers,
        honeypot,
      });
      if (!ok) throw new Error("submit failed");
      setState("done");
      captureConversion("beta_survey_submit", {
        group: "FULL",
        form_variant: "short10",
      });
    } catch {
      setState("error");
    }
  };

  return (
    <EditorialPage>
      <div className="max-w-xl mx-auto px-6 md:px-0 pt-12 pb-24">
        <div className="mb-10">
          <Link to="/" aria-label="LetsLoop home">
            <img
              src="/letsloop-logo.png"
              alt="LetsLoop"
              className="h-7 w-auto"
            />
          </Link>
        </div>

        {state === "done" ? (
          <div className="border-2 border-ink bg-paper-tan p-6 shadow-[6px_6px_0_0_rgba(5,5,5,1)]">
            <p className="font-serif text-2xl text-ink">Thank you.</p>
            <p className="text-gray-700 mt-1">
              Really appreciate you taking the time. It goes straight to the
              founders.
            </p>
          </div>
        ) : (
          <>
            <p
              className="text-2xl md:text-3xl text-salmon mb-2 -rotate-2 origin-left"
              style={handwritten}
            >
              hey{name ? ` ${name}` : ""}
            </p>
            <h1 className="font-serif text-ink tracking-tight leading-[1.05] text-3xl md:text-4xl mb-2">
              10 quick ones about LetsLoop
            </h1>
            <p className="text-sm text-gray-600 mb-8">
              Nothing here is required, answer whatever you fancy and skip the
              rest.
            </p>

            <form onSubmit={submit} className="space-y-8">
              <Honeypot value={honeypot} setValue={setHoneypot} />

              {QUESTIONS.map((q, i) => (
                <div key={q.id}>
                  <label className="block text-ink font-semibold mb-2.5 leading-snug">
                    {i + 1}. {q.prompt}
                  </label>
                  {q.kind === "text" && (
                    <TextInput
                      value={answers[q.id] || ""}
                      onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
                    />
                  )}
                  {q.kind === "choice" && (
                    <ChoiceInput
                      options={q.options}
                      value={answers[q.id]}
                      onChange={(opt) =>
                        setAnswers((a) => ({ ...a, [q.id]: opt }))
                      }
                    />
                  )}
                  {q.kind === "multichoice" && (
                    <MultiChoiceInput
                      options={q.options}
                      values={multiAnswers[q.id] || []}
                      onChange={(next) =>
                        setMultiAnswers((a) => ({ ...a, [q.id]: next }))
                      }
                    />
                  )}
                  {q.kind === "scale" && (
                    <ScaleInput
                      value={scores[q.score]}
                      onChange={(n) =>
                        setScores((s) => ({ ...s, [q.score]: n }))
                      }
                      lowLabel={q.lowLabel}
                      highLabel={q.highLabel}
                    />
                  )}
                </div>
              ))}

              <button
                type="submit"
                disabled={state === "loading"}
                className="w-full h-12 border-2 border-ink bg-ink text-white font-semibold shadow-[4px_4px_0_0_rgba(255,160,122,1)] disabled:opacity-40 disabled:shadow-none transition-opacity"
              >
                {state === "loading" ? "Sending…" : "Send it"}
              </button>
              {state === "error" && (
                <p className="text-sm text-red-600">
                  Something went wrong, mind trying again in a sec?
                </p>
              )}
            </form>
          </>
        )}
      </div>
    </EditorialPage>
  );
};

export default SurveyShortPage;
